/**
 * Safely parse a job date, handling multiple formats across all browsers.
 *
 * The iOS app has used two different formats for `formattedDate`:
 *   - Old: ISO 8601  "2026-03-12T19:02:09.245Z"   (works everywhere)
 *   - New: 12-hour   "2026-03-14 3:12:31 pm"       (fails in Safari)
 *
 * This function tries native parsing first, then manually parses the 12-hour
 * format, and finally falls back to the Firestore `timestamp` field.
 *
 * @param {string|null} initialTime  - The formattedDate string from the job
 * @param {Object|string|null} timestamp - The Firestore timestamp field (may be a Timestamp object or ISO string)
 * @returns {Date|null} A valid Date object, or null if nothing could be parsed
 */
export function parseJobDate(initialTime, timestamp) {
    // Attempt 1: native parse of initialTime (works for ISO 8601 in all browsers,
    // and for 12-hour format in Chrome/V8)
    if (initialTime) {
        const d = new Date(initialTime);
        if (!isNaN(d.getTime())) return d;

        // Attempt 2: manually parse "YYYY-MM-DD H:MM:SS am/pm"
        const parsed = parse12HourDate(initialTime);
        if (parsed) return parsed;
    }

    // Attempt 3: use the Firestore timestamp field as fallback
    if (timestamp) {
        // Firestore Timestamp object (has .toDate())
        if (typeof timestamp.toDate === 'function') {
            return timestamp.toDate();
        }
        // ISO string or other parseable string
        const d = new Date(timestamp);
        if (!isNaN(d.getTime())) return d;
    }

    return null;
}

/**
 * Parse a 12-hour date string like "2026-03-14 3:12:31 pm".
 * Safari/WebKit does not handle this natively.
 *
 * @param {string} str
 * @returns {Date|null}
 */
function parse12HourDate(str) {
    // Match: "YYYY-MM-DD H:MM:SS am/pm" (seconds optional)
    const match = str.match(
        /^(\d{4})-(\d{1,2})-(\d{1,2})\s+(\d{1,2}):(\d{2})(?::(\d{2}))?\s*(am|pm)$/i
    );
    if (!match) return null;

    const [, year, month, day, rawHour, minute, second, meridiem] = match;
    let hour = parseInt(rawHour, 10);

    if (meridiem.toLowerCase() === 'pm' && hour !== 12) hour += 12;
    if (meridiem.toLowerCase() === 'am' && hour === 12) hour = 0;

    const d = new Date(
        parseInt(year, 10),
        parseInt(month, 10) - 1,
        parseInt(day, 10),
        hour,
        parseInt(minute, 10),
        second ? parseInt(second, 10) : 0
    );

    return isNaN(d.getTime()) ? null : d;
}
